import React, { useState, useEffect } from 'react';
import { ArrowLeft, Phone, AlertTriangle, Calendar, TrendingDown, Loader2, Search, X } from 'lucide-react';
import { metricsAPI, handleMetricsAPIError, metricsFormatters } from './api';

const DefaultersView = ({ isDark, onBack, trackMonthly = false }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [defaultersData, setDefaultersData] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDefaulter, setSelectedDefaulter] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  useEffect(() => {
    fetchDefaultersData();
  }, []);

  const fetchDefaultersData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await metricsAPI.getDefaultersSummary();
      
      if (response.success) {
        setDefaultersData(response.data);
      } else {
        setError('Failed to load defaulters data');
      }
    } catch (err) {
      setError(handleMetricsAPIError(err));
    } finally {
      setLoading(false);
    }
  };

  const fetchDefaulterDetails = async (defaulterId) => {
    try {
      const response = await metricsAPI.getDefaulterDetails(defaulterId);
      
      if (response.success) {
        setSelectedDefaulter(response.data);
        setShowDetailsModal(true);
      }
    } catch (err) {
      console.error('Error fetching defaulter details:', err);
    }
  };

  const filteredDefaulters = () => {
    if (!defaultersData || !defaultersData.active_defaulters) return [];
    
    let filtered = defaultersData.active_defaulters;

    // Filter by trackMonthly if specified
    if (trackMonthly) {
      filtered = filtered.filter(d => d.can_track_monthly);
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(d =>
        d.member_name.toLowerCase().includes(query) ||
        d.member_id.toLowerCase().includes(query) ||
        d.group.toLowerCase().includes(query)
      );
    }

    return filtered;
  };

  const getRiskLevel = (daysInDefault) => {
    if (daysInDefault >= 90) return { label: 'Critical', color: 'red' };
    if (daysInDefault >= 60) return { label: 'High', color: 'orange' };
    if (daysInDefault >= 30) return { label: 'Medium', color: 'yellow' };
    return { label: 'Low', color: 'green' };
  };

  if (loading) {
    return (
      <div>
        <button
          onClick={onBack}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors mb-4 ${
            isDark 
              ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' 
              : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
          }`}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Performance Analytics
        </button>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <span className={`ml-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Loading defaulters data...
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <button
          onClick={onBack}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors mb-4 ${
            isDark 
              ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' 
              : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
          }`}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Performance Analytics
        </button>
        <div className={`rounded-lg p-6 ${isDark ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'}`}>
          <p className={isDark ? 'text-red-400' : 'text-red-600'}>{error}</p>
          <button 
            onClick={fetchDefaultersData}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const filtered = filteredDefaulters();

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={onBack}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors mb-4 ${
            isDark 
              ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' 
              : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
          }`}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Performance Analytics
        </button>
        <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          {trackMonthly 
            ? 'Members with loans disbursed from Sept 2025 onwards (monthly payment tracking available)'
            : 'All members currently in arrears on their loan payments'
          }
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className={`p-4 rounded-lg ${isDark ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'}`}>
          <div className={`text-sm mb-1 ${isDark ? 'text-red-400' : 'text-red-600'}`}>
            {trackMonthly ? 'Trackable Defaulters' : 'Total Active Defaulters'}
          </div>
          <div className="text-2xl font-bold text-red-600">
            {filtered.length}
          </div>
        </div>
        <div className={`p-4 rounded-lg ${isDark ? 'bg-[#2b323c] border border-gray-700' : 'bg-white border border-gray-200'}`}>
          <div className={`text-sm mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total Arrears</div>
          <div className="text-2xl font-bold text-orange-600">
            {metricsFormatters.formatCurrency(
              filtered.reduce((sum, d) => sum + parseFloat(d.default_amount), 0)
            )}
          </div>
        </div>
        <div className={`p-4 rounded-lg ${isDark ? 'bg-[#2b323c] border border-gray-700' : 'bg-white border border-gray-200'}`}>
          <div className={`text-sm mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Avg Days in Default</div>
          <div className="text-2xl font-bold text-purple-600">
            {Math.round(
              filtered.reduce((sum, d) => sum + d.days_in_default, 0) / (filtered.length || 1)
            )} days
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-4 relative">
        <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
        <input
          type="text"
          placeholder="Search by member name, ID, or group..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={`w-full pl-10 pr-4 py-2 border rounded-lg transition-colors ${
            isDark 
              ? 'bg-[#2b323c] border-gray-700 text-gray-200 placeholder-gray-500 focus:border-blue-500' 
              : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500'
          } focus:outline-none focus:ring-2 focus:ring-blue-500`}
        />
      </div>

      {/* Defaulters Table */}
      <div className={`rounded-lg shadow-sm border overflow-hidden ${isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={`border-b ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
              <tr>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Member
                </th>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Group
                </th>
                <th className={`px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Arrears
                </th>
                <th className={`px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Months Behind
                </th>
                <th className={`px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Days in Default
                </th>
                <th className={`px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Risk Level
                </th>
                {trackMonthly && (
                  <th className={`px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
              {filtered.map((defaulter, index) => {
                const risk = getRiskLevel(defaulter.days_in_default);
                return (
                  <tr key={index} className={isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                    <td className="px-6 py-4">
                      <div>
                        <div className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                          {defaulter.member_name}
                        </div>
                        <div className={`text-xs flex items-center gap-1 mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          <Phone className="w-3 h-3" />
                          {defaulter.phone || 'N/A'}
                        </div>
                        <div className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                          ID: {defaulter.member_id}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        {defaulter.group}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className="text-sm font-semibold text-red-600">
                        {metricsFormatters.formatCurrency(defaulter.default_amount)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        isDark ? 'bg-orange-900/30 text-orange-400' : 'bg-orange-100 text-orange-800'
                      }`}>
                        {defaulter.months_behind} months
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        {defaulter.days_in_default} days
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        risk.color === 'red' ? 'bg-red-100 text-red-800' :
                        risk.color === 'orange' ? 'bg-orange-100 text-orange-800' :
                        risk.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {risk.label}
                      </span>
                    </td>
                    {trackMonthly && (
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => fetchDefaulterDetails(defaulter.id)}
                          className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                        >
                          View Details
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filtered.length === 0 && (
          <div className={`text-center py-12 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            <AlertTriangle className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
            <p className="text-lg">
              {searchQuery ? 'No defaulters found matching your search.' : 'No defaulters found.'}
            </p>
          </div>
        )}
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedDefaulter && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className={`max-w-4xl w-full rounded-lg shadow-xl ${isDark ? 'bg-[#2b323c]' : 'bg-white'}`}>
            <div className={`flex items-center justify-between p-6 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Defaulter Details - {selectedDefaulter.member.name}
              </h3>
              <button
                onClick={() => setShowDetailsModal(false)}
                className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
              >
                <X className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
              </button>
            </div>

            <div className="p-6 max-h-[70vh] overflow-y-auto">
              {/* Member & Loan Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <h4 className={`text-sm font-semibold mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Member Information
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Name:</span>
                      <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                        {selectedDefaulter.member.name}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Phone:</span>
                      <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                        {selectedDefaulter.member.phone}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Group:</span>
                      <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                        {selectedDefaulter.member.group}
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className={`text-sm font-semibold mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Loan Information
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Amount:</span>
                      <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                        {metricsFormatters.formatCurrency(selectedDefaulter.loan.amount)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Total Repayment:</span>
                      <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                        {metricsFormatters.formatCurrency(selectedDefaulter.loan.total_repayment)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Disbursed:</span>
                      <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                        {selectedDefaulter.loan.disbursed_at}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Default Info */}
              <div className={`p-4 rounded-lg mb-6 ${isDark ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'}`}>
                <h4 className={`text-sm font-semibold mb-3 ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                  Default Status
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Arrears</div>
                    <div className="text-lg font-bold text-red-600">
                      {metricsFormatters.formatCurrency(selectedDefaulter.default_info.default_amount)}
                    </div>
                  </div>
                  <div>
                    <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Days in Default</div>
                    <div className={`text-lg font-bold ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                      {selectedDefaulter.default_info.days_in_default}
                    </div>
                  </div>
                  <div>
                    <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Expected Payment</div>
                    <div className={`text-lg font-bold ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                      {metricsFormatters.formatCurrency(selectedDefaulter.default_info.expected_payment)}
                    </div>
                  </div>
                  <div>
                    <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Current Balance</div>
                    <div className={`text-lg font-bold ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                      {metricsFormatters.formatCurrency(selectedDefaulter.default_info.current_balance)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Monthly Details */}
              {selectedDefaulter.monthly_details && selectedDefaulter.monthly_details.length > 0 && (
                <div>
                  <h4 className={`text-sm font-semibold mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Monthly Payment History
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className={`border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                        <tr>
                          <th className={`text-left py-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Month</th>
                          <th className={`text-right py-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Expected</th>
                          <th className={`text-right py-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Actual</th>
                          <th className={`text-right py-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Deficit</th>
                          <th className={`text-center py-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Status</th>
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                        {selectedDefaulter.monthly_details.map((detail, idx) => (
                          <tr key={idx}>
                            <td className={`py-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                              {detail.month}
                            </td>
                            <td className={`py-2 text-right ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                              {metricsFormatters.formatCurrency(detail.expected_payment)}
                            </td>
                            <td className={`py-2 text-right ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                              {metricsFormatters.formatCurrency(detail.actual_payment)}
                            </td>
                            <td className="py-2 text-right">
                              <span className={detail.deficit > 0 ? 'text-red-600 font-medium' : 'text-green-600'}>
                                {metricsFormatters.formatCurrency(detail.deficit)}
                              </span>
                            </td>
                            <td className="py-2 text-center">
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                detail.is_missed 
                                  ? 'bg-red-100 text-red-800' 
                                  : detail.is_partial 
                                  ? 'bg-yellow-100 text-yellow-800' 
                                  : 'bg-green-100 text-green-800'
                              }`}>
                                {detail.is_missed ? 'Missed' : detail.is_partial ? 'Partial' : 'Paid'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DefaultersView;